import React, {useState} from 'react';
import {curry} from 'ramda';
import {FrameRate} from '@accurate-player/accurate-player-core';

import {OnStringSetState} from '@pluto/@types';
import {TimespanCategory} from '@pluto/rest';
import {MarkupMarker} from '../../../Timeline/TimelineTypes';
import Table, {MarkupData} from './Table';

export type OnButtonClick = (data: MarkupData) => void;
export type OnTypeChange = (data: MarkupData, type: number) => void;
export type OnFrameChange = (data: MarkupData, frame: number) => void;
export type OnEditMarkupMarkerProps = {
    marker: MarkupMarker;
    inpoint?: boolean;
};
export type OnEditMarkupMarker = (props: OnEditMarkupMarkerProps) => void;

export interface TableContainerProps {
    markers: MarkupMarker[];
    noteTypes: TimespanCategory[];
    selectedMarkerId: string;
    frameRate: FrameRate;
    dropFrame: boolean;
    duration: number;
    onMarkerClick: (marker: MarkupData) => void;
    onDeleteClick: OnButtonClick;
    onEditMarker: OnEditMarkupMarker;
    jobDeliveryDate: string;
}

export type OnRowClickProps = {
    onMarkerClick: (marker: MarkupData) => void;
    setSelectedColumn: OnStringSetState;
};

export const onRowClick = curry(
    (props: OnRowClickProps, event: MouseEvent, item: MarkupData, column: string) => {
        const {onMarkerClick, setSelectedColumn} = props;

        event.stopPropagation();
        event.preventDefault();

        onMarkerClick(item);
        setSelectedColumn(column);
    }
);

export const onTypeChange = curry(
    (onEditMarker: OnEditMarkupMarker, markupData: MarkupData, updatedIssue: number) => {
        const {identifier, start, end, issue} = markupData;

        if (issue === updatedIssue) return;

        const marker: MarkupMarker = {
            identifier,
            start,
            end,
            issue: updatedIssue,
        };

        onEditMarker({
            marker,
        });
    }
);

export const onStartFrameChange = curry(
    (onEditMarker: OnEditMarkupMarker, markupData: MarkupData, updatedStart: number) => {
        const {identifier, start, end, issue} = markupData;

        if (start === updatedStart) return;

        const marker: MarkupMarker = {
            identifier,
            start: updatedStart,
            end,
            issue,
        };

        onEditMarker({
            marker,
            inpoint: true,
        });
    }
);

export const onEndFrameChange = curry(
    (onEditMarker: OnEditMarkupMarker, markupData: MarkupData, updatedEnd: number) => {
        const {identifier, start, end, issue} = markupData;

        if (end === updatedEnd) return;

        const marker: MarkupMarker = {
            identifier,
            start,
            end: updatedEnd,
            issue,
        };

        onEditMarker({
            marker,
            inpoint: false,
        });
    }
);

export default function TableContainer(props: TableContainerProps) {
    const {
        markers,
        noteTypes,
        selectedMarkerId,
        frameRate,
        dropFrame,
        duration,
        onMarkerClick,
        onDeleteClick,
        onEditMarker,
        jobDeliveryDate,
    } = props;
    const [selectedColumn, setSelectedColumn] = useState('');

    const handleRowClick = onRowClick({
        onMarkerClick,
        setSelectedColumn,
    });
    const handleTypeChange = onTypeChange(onEditMarker);
    const handleStartFrameChange = onStartFrameChange(onEditMarker);
    const handleEndFrameChange = onEndFrameChange(onEditMarker);

    return (
        <Table
            jobDeliveryDate={jobDeliveryDate}
            markers={markers}
            noteTypes={noteTypes}
            selectedMarkerId={selectedMarkerId}
            selectedColumn={selectedColumn}
            frameRate={frameRate}
            dropFrame={dropFrame}
            duration={duration}
            onRowClick={handleRowClick}
            onDeleteClick={onDeleteClick}
            onTypeChange={handleTypeChange}
            onStartFrameChange={handleStartFrameChange}
            onEndFrameChange={handleEndFrameChange}
        />
    );
}
